import { memo, useMemo } from "react";
import { Check, Copy, Eye, EyeOff, Pencil, Trash2, X } from "lucide-react";
import type { ScheduleEntry } from "@/data/types";
import { MODE_CATALOG, MODE_TONE } from "@/data/modeCatalog";
import { cn } from "@/lib/cn";
import { DAYS_SHORT, fmtHm } from "./scheduleLib";

/**
 * Table mirror of the calendar — same entries, different surface. Lets the
 * operator scan everything at once and drive single-entry actions (edit /
 * clone / disable / delete) keyboard-style without hunting for the right
 * block on the grid.
 *
 * The table is sorted by priority desc (matching the resolver's tie-break);
 * disabled entries are dimmed but still listed so they can be re-enabled.
 */
export const EntryList = memo(function EntryList({
  entries,
  editingId,
  onEdit,
  onClone,
  onToggleEnabled,
  onRemove,
}: {
  entries: ScheduleEntry[];
  editingId: string | null;
  onEdit: (id: string) => void;
  onClone: (id: string) => void;
  onToggleEnabled: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  const sortedEntries = useMemo(
    () => entries.slice().sort((a, b) => (b.priority - a.priority) || a.id.localeCompare(b.id)),
    [entries],
  );
  if (entries.length === 0) {
    return (
      <div className="px-1 py-6 text-center text-xs text-text-muted">
        No entries yet. Click anywhere on the calendar above (or use{" "}
        <strong className="text-text">Add entry</strong>) to create one.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-text-faint uppercase text-[10px] tracking-wide">
          <tr>
            <th className="px-2 py-2 text-left w-6"></th>
            <th className="px-2 py-2 text-left">Id / name</th>
            <th className="px-2 py-2 text-left">Days</th>
            <th className="px-2 py-2 text-left">Window</th>
            <th className="px-2 py-2 text-left">Mode</th>
            <th className="px-2 py-2 text-left">Priority</th>
            <th className="px-2 py-2 text-left">Dates</th>
            <th className="px-2 py-2 text-right"></th>
          </tr>
        </thead>
        <tbody>
          {sortedEntries.map((e) => {
              const isEditing = editingId === e.id;
              const wrap = e.end_min !== 0 && e.end_min <= e.start_min;
              return (
                <tr
                  key={e.id}
                  className={cn(
                    "border-t border-edge/40 text-xs",
                    isEditing && "bg-accent/5",
                    !e.enabled && "opacity-50",
                  )}
                >
                  <td className="px-2 py-2">
                    <span
                      className={cn(
                        "inline-block h-2.5 w-2.5 rounded-full",
                        e.enabled ? "bg-ok" : "bg-text-faint",
                      )}
                      aria-label={e.enabled ? "Enabled" : "Disabled"}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <div className="font-mono text-text">{e.id}</div>
                    {e.name && <div className="text-[11px] text-text-faint">{e.name}</div>}
                  </td>
                  <td className="px-2 py-2 text-text-muted">
                    {e.days_of_week.length === 7
                      ? "every day"
                      : e.days_of_week.length === 5 && e.days_of_week.every((d, i) => d === i)
                        ? "weekdays"
                        : e.days_of_week.length === 2 && e.days_of_week[0] === 5 && e.days_of_week[1] === 6
                          ? "weekends"
                          : e.days_of_week.map((d) => DAYS_SHORT[d]).join(", ")}
                  </td>
                  <td className="px-2 py-2 font-mono text-text-muted">
                    {fmtHm(e.start_min)}–{fmtHm(e.end_min)}
                    {wrap && <span className="ml-1 text-text-faint">(wraps)</span>}
                  </td>
                  <td className="px-2 py-2">
                    <span className={cn("inline-flex items-center rounded border px-2 py-0.5 text-[11px] font-semibold", MODE_TONE[e.mode])}>
                      {MODE_CATALOG[e.mode].label}
                    </span>
                  </td>
                  <td className="px-2 py-2 font-mono text-text-muted">{e.priority}</td>
                  <td className="px-2 py-2 text-text-muted">
                    {e.date_start || e.date_end ? (
                      <span className="font-mono text-[11px]">
                        {e.date_start ?? "…"} → {e.date_end ?? "…"}
                      </span>
                    ) : (
                      <span className="text-text-faint">all year</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <IconBtn onClick={() => onToggleEnabled(e.id)} title={e.enabled ? "Disable" : "Enable"}>
                        {e.enabled ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </IconBtn>
                      <IconBtn onClick={() => onEdit(e.id)} title="Edit"><Pencil className="h-3.5 w-3.5" /></IconBtn>
                      <IconBtn onClick={() => onClone(e.id)} title="Duplicate"><Copy className="h-3.5 w-3.5" /></IconBtn>
                      <IconBtn onClick={() => onRemove(e.id)} title="Delete" tone="critical"><Trash2 className="h-3.5 w-3.5" /></IconBtn>
                    </div>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
});

function IconBtn({
  onClick,
  title,
  tone = "muted",
  children,
}: {
  onClick: () => void;
  title: string;
  tone?: "muted" | "critical";
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        "inline-flex h-7 w-7 items-center justify-center rounded-md border",
        tone === "critical"
          ? "border-critical/30 text-critical hover:bg-critical/10"
          : "border-edge text-text-muted hover:bg-surface-2",
      )}
    >
      {children}
    </button>
  );
}

// Unused but kept for symmetry with EntryEditor's BoolToggle (in case the
// page later inlines an enable/disable button anywhere).
export function _Indicator({ on }: { on: boolean }) {
  return on ? <Check className="h-3 w-3 text-ok" /> : <X className="h-3 w-3 text-text-faint" />;
}
